import React, {
  useState, useRef, useEffect, useCallback, useMemo, memo,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import {
  PrsButtonLg, PrsOutlineButtonLg, PrsTertiaryButtonLg, PrsButtonStackLg,
} from '../../../../styles-and-themes/styles/prsButtons';
import { PrsCheckbox } from '../../../../styles-and-themes/styles/prsInputs';
import { VisuallyHidden } from '../../../../styles-and-themes/styles/visually-hidden';
import {
  UamHeading,
  UamBodyText,
  UamSmallText,
  UamFlexColumn,
  UamLoadingContainer,
  UamPageIcon,
} from '../../../../styles-and-themes/styles/uam-registration-styles';
import {
  MfaPanelRoot,
  MfaPanelContent,
  MfaPanelBody,
  ResendLink,
  CooldownTimer,
  DigitBoxContainer,
  DigitBox,
  RememberDeviceLabel,
  LockoutTimerText,
  LockoutBody,
} from './MfaVerifyPanelStyles';
import ProseSpinner from '../../../../components/ProseSpinner/ProseSpinner';
import StepTransitionOverlay from '../../../../components/StepTransitionOverlay/StepTransitionOverlay';
import UamToast from '../../../../components/PrsAlert/UamToast';
import UamBanner from '../../../../components/PrsAlert/UamBanner';
import MfaCancelDialog from '../../../../components/MfaCancelDialog/MfaCancelDialog';
// Component-scoped reset; lift to App.js once verified safe across all pages.
import PrsResetGlobalStyle from '../../../../styles-and-themes/styles/prsReset';
import { frontEndUrl } from '../../../../destructured-front-end-config';
import { SUPPORT_URL } from '../../../../constants';

const NUM_DIGITS = 6;

const MFA_URL = '/api/v2/fi/token/mfa';

const REPORT_PROBLEM_ICON_URL = `${frontEndUrl}static/images/report_problem.svg`;
const TEXTSMS_ICON_URL = `${frontEndUrl}static/images/textsms.svg`;

const MfaDigit = memo(({
  index, value, disabled, error, isFirst, refSetter, onChange, onKeyDown, onPaste,
}) => (
  <DigitBox
    ref={refSetter}
    type="text"
    inputMode="numeric"
    maxLength={1}
    pattern="[0-9]*"
    value={value}
    disabled={disabled}
    error={error}
    autoComplete={isFirst ? 'one-time-code' : 'off'}
    aria-label={`Digit ${index + 1} of 6`}
    aria-invalid={error}
    onChange={(ev) => onChange(index, ev.target.value)}
    onKeyDown={(ev) => onKeyDown(index, ev)}
    onPaste={isFirst ? onPaste : undefined}
  />
));

MfaDigit.propTypes = {
  index: PropTypes.number.isRequired,
  value: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  error: PropTypes.bool.isRequired,
  isFirst: PropTypes.bool.isRequired,
  refSetter: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onKeyDown: PropTypes.func.isRequired,
  onPaste: PropTypes.func.isRequired,
};

/**
 * MFA verification panel.
 *
 * On mount: POSTs to /token/mfa with no body to trigger SMS send.
 * On submit: POSTs { code } to /token/mfa to verify.
 *   On 200 response: calls onSuccess().
 * Resend: POSTs to /token/mfa with no body again.
 *
 * maskedPhone / expiresIn may be null on initial render if the parent doesn't have them yet —
 * they are updated from the initiate response.
 */
const MfaVerifyPanel = ({
  maskedPhone: maskedPhoneProp, expiresIn: expiresInProp, onSuccess, onBack, showRememberDevice, onVerifyAnotherWay,
}) => {
  const [digits, setDigits] = useState(() => Array(NUM_DIGITS).fill(''));
  const [rememberDevice, setRememberDevice] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [locked, setLocked] = useState(false);
  const [lockedUntil, setLockedUntil] = useState(null); // epoch ms — for lockout countdown
  const [isInitiating, setIsInitiating] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  // Latch-once flags: once true, never reset for the lifetime of this panel.
  // - succeeded: verify returned 200; the parent's redirect/transition is in flight
  //   and we must not interrupt it (no prompt, no modal, no phantom history.back()).
  // - terminalError: session is dead (401); the user can't recover from this panel,
  //   so warning them about losing progress is misleading.
  const [succeeded, setSucceeded] = useState(false);
  const [terminalError, setTerminalError] = useState(false);
  const [showResendToast, setShowResendToast] = useState(false);
  // Bumped on every successful resend so the toast remounts (key change) and
  // replays its enter animation, even if a previous toast is still visible.
  const [resendAttempt, setResendAttempt] = useState(0);
  const handleResendToastClose = useCallback(() => setShowResendToast(false), []);
  const [cooldownSecs, setCooldownSecs] = useState(0);
  const [maskedPhone, setMaskedPhone] = useState(maskedPhoneProp || null);
  const [expiresIn, setExpiresIn] = useState(expiresInProp || null);
  // When non-null, the cancel-confirmation dialog is open and pendingCancelAction
  // is the function to invoke when the user confirms cancellation.
  const [pendingCancelAction, setPendingCancelAction] = useState(null);
  const inputRefs = useRef([]);
  const cooldownRef = useRef(null);

  const inputRefSetters = useMemo(
    () => Array.from({ length: NUM_DIGITS }, (_, i) => (el) => { inputRefs.current[i] = el; }),
    [],
  );

  const startCooldown = useCallback((secs) => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
    setCooldownSecs(secs);
    cooldownRef.current = setInterval(() => {
      setCooldownSecs((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => () => {
    if (cooldownRef.current) clearInterval(cooldownRef.current);
  }, []);

  // While the user is mid-verification, warn on tab close / reload via the
  // browser's native unsaved-changes prompt. Skipped during the initial OTP
  // request, after lockout, while a verify submission is in flight, after a
  // successful verify (parent redirect is inbound), and once the session is
  // terminally dead (401) — in all these cases there's no in-progress work
  // worth warning about.
  useEffect(() => {
    if (isInitiating || locked || isSubmitting || succeeded || terminalError) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isInitiating, locked, isSubmitting, succeeded, terminalError]);

  // Browser back-button: push a sentinel history entry so back fires popstate
  // instead of leaving the page, then open the cancel-confirmation dialog. On
  // confirm we pop the sentinel and run onBack; on dismiss the sentinel is
  // re-pushed to keep the user on the MFA step. Stable refs for onBack and for
  // the "is this still an interruptable state?" inputs so the listener — bound
  // once per effect run — stays correct without re-binding on every state change.
  const onBackRef = useRef(onBack);
  useEffect(() => { onBackRef.current = onBack; }, [onBack]);
  const isSubmittingRef = useRef(isSubmitting);
  useEffect(() => { isSubmittingRef.current = isSubmitting; }, [isSubmitting]);
  const succeededRef = useRef(succeeded);
  useEffect(() => { succeededRef.current = succeeded; }, [succeeded]);
  const terminalErrorRef = useRef(terminalError);
  useEffect(() => { terminalErrorRef.current = terminalError; }, [terminalError]);
  const skipNextPopRef = useRef(false);
  useEffect(() => {
    if (isInitiating || locked) return undefined;
    window.history.pushState({ mfaCancelSentinel: true }, '');

    const onPopState = () => {
      if (skipNextPopRef.current) {
        skipNextPopRef.current = false;
        return;
      }
      // Mid-submit, post-success, or terminal-error states are not interruptable:
      // a verify is committed and will land on success/lockout in a moment, or the
      // redirect is already inbound, or the session is dead. In any of these we let
      // the back navigate without re-pushing the sentinel and without opening the
      // modal — there's nothing to confirm.
      if (isSubmittingRef.current || succeededRef.current || terminalErrorRef.current) {
        return;
      }
      // User pressed back — re-push the sentinel so the URL stays put, then
      // open the dialog. Confirm just calls onBack; the unmount cleanup is
      // responsible for popping the sentinel (single source of truth — calling
      // history.back() here too races the cleanup and can over-navigate).
      window.history.pushState({ mfaCancelSentinel: true }, '');
      setPendingCancelAction(() => () => onBackRef.current());
    };

    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
      // On a successful unmount, skip the back-pop: the parent's redirect or
      // step transition is already in flight and a phantom history.back() here
      // is a back-stack mutation the user didn't ask for.
      if (window.history.state?.mfaCancelSentinel && !succeededRef.current) {
        skipNextPopRef.current = true;
        window.history.back();
      }
    };
  }, [isInitiating, locked]);

  const code = digits.join('');
  const isFormValid = code.length === NUM_DIGITS && !locked;

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      try {
        const res = await axios.post(
          MFA_URL,
          { initiate: true },
          { validateStatus: (s) => s < 500 || s >= 600, signal: controller.signal },
        );
        if (res.status === 200) {
          if (res.data?.maskedPhone) setMaskedPhone(res.data.maskedPhone);
          if (res.data?.expiresIn) setExpiresIn(res.data.expiresIn);
          if (res.data?.resendCooldown) startCooldown(res.data.resendCooldown);
        } else if (res.status === 429) {
          const body = res.data || {};
          if (body.maskedPhone) setMaskedPhone(body.maskedPhone);
          if (body.expiresIn) setExpiresIn(body.expiresIn);
          if (body.locked) {
            setLocked(true);
            if (body.lockedUntil) setLockedUntil(body.lockedUntil);
          } else if (body.maxResendsExceeded) setErrorMessage('Maximum resends reached. Please sign in again to request a new code.');
          else if (body.cooldown) {
            if (typeof body.retryAfter === 'number') startCooldown(body.retryAfter);
          } else setErrorMessage('Too many attempts. Please try again later.');
        } else if (res.status === 401) {
          setTerminalError(true);
          terminalErrorRef.current = true;
          setErrorMessage('Your verification session has expired. Please sign in again.');
        } else {
          setErrorMessage('Failed to send verification code. Please try again.');
        }
      } catch (err) {
        if (axios.isCancel(err)) return;
        console.error('MFA initiate failed', err);
        setErrorMessage('Failed to send verification code. Please try again.');
      } finally {
        if (!controller.signal.aborted) setIsInitiating(false);
      }
    })();
    return () => controller.abort();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDigitChange = useCallback((index, value) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });
    if (digit && index < NUM_DIGITS - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, []);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }, []);

  const handlePaste = useCallback((e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, NUM_DIGITS);
    if (!pasted) return;
    e.preventDefault();
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < pasted.length; i++) next[i] = pasted[i];
      return next;
    });
    inputRefs.current[Math.min(pasted.length, NUM_DIGITS - 1)]?.focus();
  }, []);

  const onSubmit = async (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    if (!isFormValid || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage(null);
    setAttemptsRemaining(null);

    try {
      const res = await axios.post(
        MFA_URL,
        { code, rememberDevice },
        { validateStatus: (s) => s < 500 || s >= 600 },
      );

      if (res.status === 200 || res.status === 204) {
        // Latch succeeded BEFORE awaiting onSuccess: the parent may unmount us
        // synchronously off this state change, and the cleanup needs to read
        // succeededRef.current — useEffect-driven ref sync wouldn't have run yet.
        setSucceeded(true);
        succeededRef.current = true;
        // Await onSuccess so the parent's transition work (e.g. another flow API call)
        // happens while isSubmitting is still true — keeps the overlay up across steps.
        await onSuccess();
        return;
      }

      if (res.status === 429) {
        setLocked(true);
        if (res.data?.lockedUntil) setLockedUntil(res.data.lockedUntil);
        return;
      }

      if (res.status === 401) {
        setTerminalError(true);
        terminalErrorRef.current = true;
        setErrorMessage('Your verification session has expired. Please sign in again.');
        return;
      }

      const msg = res.data?.message || 'Invalid code. Please try again.';
      setErrorMessage(msg);
      if (res.data?.attemptsRemaining !== undefined) setAttemptsRemaining(res.data.attemptsRemaining);
      setDigits(Array(NUM_DIGITS).fill(''));
      inputRefs.current[0]?.focus();
    } catch (err) {
      console.error('MFA verify failed', err);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const onResend = async () => {
    if (isResending) return;
    setIsResending(true);
    setErrorMessage(null);
    setAttemptsRemaining(null);

    try {
      const res = await axios.post(
        MFA_URL,
        { resend: true },
        { validateStatus: (s) => s < 500 || s >= 600 },
      );

      if (res.status === 200) {
        setShowResendToast(true);
        setResendAttempt((c) => c + 1);
        setDigits(Array(NUM_DIGITS).fill(''));
        inputRefs.current[0]?.focus();
        if (res.data?.maskedPhone) setMaskedPhone(res.data.maskedPhone);
        if (res.data?.expiresIn) setExpiresIn(res.data.expiresIn);
        if (res.data?.resendCooldown) startCooldown(res.data.resendCooldown);
        return;
      }

      if (res.status === 429) {
        const body = res.data || {};
        if (body.maskedPhone) setMaskedPhone(body.maskedPhone);
        if (body.expiresIn) setExpiresIn(body.expiresIn);
        if (body.locked) {
          setLocked(true);
          if (body.lockedUntil) setLockedUntil(body.lockedUntil);
        } else if (body.maxResendsExceeded) setErrorMessage('Maximum resends reached. Please sign in again to request a new code.');
        else if (body.cooldown) {
          if (typeof body.retryAfter === 'number') startCooldown(body.retryAfter);
        } else setErrorMessage('Too many attempts. Please try again later.');
        return;
      }

      if (res.status === 401) {
        setTerminalError(true);
        terminalErrorRef.current = true;
        setErrorMessage('Your verification session has expired. Please sign in again.');
        return;
      }

      setErrorMessage('Failed to resend code. Please try again.');
    } catch (err) {
      console.error('MFA resend failed', err);
      setErrorMessage('Failed to resend code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const isWrongCodeError = attemptsRemaining !== null;

  // Lockout countdown: recompute remaining time from lockedUntil every second
  const [lockoutSecsRemaining, setLockoutSecsRemaining] = useState(null);
  useEffect(() => {
    if (!lockedUntil) return undefined;
    const tick = () => {
      const secs = Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));
      setLockoutSecsRemaining(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const formatLockoutTime = (secs) => {
    if (secs == null) return null;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  // Move keyboard focus onto each branch's heading as it mounts. Using a callback
  // ref means we don't fight React's render cycle — the focus call lands after
  // the DOM node exists. tabIndex=-1 makes the heading programmatically focusable
  // without inserting it into the tab order.
  const focusHeadingOnMount = useCallback((node) => {
    if (!node) return;
    if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '-1');
    node.focus({ preventScroll: false });
  }, []);

  if (isInitiating) {
    return (
      <>
        <PrsResetGlobalStyle />
        <UamLoadingContainer>
          <ProseSpinner size="5rem" />
        </UamLoadingContainer>
      </>
    );
  }

  if (locked) {
    return (
      <>
        <PrsResetGlobalStyle />
        <UamFlexColumn gap="16px">
          <UamPageIcon src={REPORT_PROBLEM_ICON_URL} alt="" />
          <UamHeading ref={focusHeadingOnMount}>We couldn&apos;t verify your identity.</UamHeading>
          <LockoutBody>
            <UamBodyText>
              You&apos;ve reached the maximum number of verification attempts. Please try again after 20 minutes.
            </UamBodyText>
            {lockoutSecsRemaining != null && lockoutSecsRemaining > 0 && (
              <>
                {/* Per-second display for sighted users; aria-hidden so SR users
                    don't get a one-per-second flood of announcements. */}
                <LockoutTimerText id="mfa-lockout-timer" aria-hidden="true">
                  {formatLockoutTime(lockoutSecsRemaining)}
                </LockoutTimerText>
                {/* Minute-resolution status for AT — text only changes once per
                    minute, so SR announces at minute boundaries instead of every tick. */}
                <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
                  {`${Math.ceil(lockoutSecsRemaining / 60)} minute${Math.ceil(lockoutSecsRemaining / 60) === 1 ? '' : 's'} remaining`}
                </VisuallyHidden>
              </>
            )}
            <UamBodyText>
              {'If the issue continues, '}
              <a href={SUPPORT_URL} style={{ color: '#0600b4', textDecoration: 'underline' }}>contact support</a>
              {' for help verifying your account.'}
            </UamBodyText>
          </LockoutBody>
          <PrsButtonStackLg>
            <PrsButtonLg type="button" style={{ width: '100%' }} onClick={onBack}>
              Back to Sign In
            </PrsButtonLg>
          </PrsButtonStackLg>
        </UamFlexColumn>
      </>
    );
  }

  return (
    <>
      <PrsResetGlobalStyle />
      <StepTransitionOverlay loading={isSubmitting || isResending}>
        <MfaPanelRoot>
          {(isWrongCodeError || errorMessage) && (
          <UamBanner variant="danger">
            {isWrongCodeError ? (
              <>
                {"That code isn't correct. Please check the text message and try again. You have "}
                <strong>{`${attemptsRemaining} ${attemptsRemaining === 1 ? 'attempt' : 'attempts'}`}</strong>
                {' remaining.'}
              </>
            ) : errorMessage}
          </UamBanner>
          )}

          <UamToast
            key={resendAttempt}
            show={showResendToast}
            variant="success"
            autoDismissMs={5000}
            onClose={handleResendToastClose}
          >
            A new verification code has been sent
          </UamToast>

          <MfaPanelContent>
            <UamPageIcon src={TEXTSMS_ICON_URL} alt="" />

            <UamHeading ref={focusHeadingOnMount}>Verify your phone number.</UamHeading>

            <MfaPanelBody>
              <UamBodyText>
                {'We sent a verification code to '}
                <strong id="mfa-masked-phone" style={{ wordBreak: 'break-all' }}>{maskedPhone || '...'}</strong>
                {expiresIn ? (
                  <>
                    {'. Enter it below to continue. This code will expire in '}
                    <span id="mfa-expiry-minutes" style={{ display: 'inline-block' }}><strong>{Math.ceil(expiresIn / 60)}</strong></span>
                    {' minutes.'}
                  </>
                ) : '.'}
              </UamBodyText>

              <UamSmallText>
                {'Need a new code? '}
                <ResendLink
                  as="button"
                  type="button"
                  onClick={onResend}
                  disabled={isResending || locked || cooldownSecs > 0}
                  $cooling={cooldownSecs > 0}
                >
                  {isResending ? 'Sending...' : 'Resend code'}
                </ResendLink>
                {cooldownSecs > 0 && <CooldownTimer id="mfa-resend-cooldown-timer" aria-hidden="true">({cooldownSecs}s)</CooldownTimer>}
              </UamSmallText>

              <DigitBoxContainer
                role="group"
                aria-label="Verification code"
                aria-describedby={`mfa-masked-phone${expiresIn ? ' mfa-expiry-minutes' : ''}`}
              >
                {digits.map((digit, i) => (
                  <MfaDigit
                    key={i} // eslint-disable-line react/no-array-index-key
                    index={i}
                    value={digit}
                    disabled={locked}
                    error={isWrongCodeError || !!errorMessage}
                    isFirst={i === 0}
                    refSetter={inputRefSetters[i]}
                    onChange={handleDigitChange}
                    onKeyDown={handleKeyDown}
                    onPaste={handlePaste}
                  />
                ))}
              </DigitBoxContainer>

              {showRememberDevice && (
              <RememberDeviceLabel htmlFor="remember-device">
                <PrsCheckbox
                  id="remember-device"
                  checked={rememberDevice}
                  onChange={(ev) => setRememberDevice(ev.target.checked)}
                />
                Remember this device
              </RememberDeviceLabel>
              )}
            </MfaPanelBody>
          </MfaPanelContent>

          <form onSubmit={onSubmit}>
            <PrsButtonStackLg>
              <PrsButtonLg
                type="submit"
                style={{ width: '100%' }}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? 'Verifying...' : 'Verify'}
              </PrsButtonLg>

              {onVerifyAnotherWay && (
              <PrsOutlineButtonLg
                type="button"
                style={{ width: '100%' }}
                onClick={onVerifyAnotherWay}
              >
                Verify Another Way
              </PrsOutlineButtonLg>
              )}

              {onVerifyAnotherWay ? (
                <PrsTertiaryButtonLg
                  type="button"
                  style={{ width: '100%' }}
                  onClick={() => setPendingCancelAction(() => onBack)}
                >
                  Back to Sign In
                </PrsTertiaryButtonLg>
              ) : (
                <PrsOutlineButtonLg
                  type="button"
                  style={{ width: '100%' }}
                  onClick={() => setPendingCancelAction(() => onBack)}
                >
                  Back to Sign In
                </PrsOutlineButtonLg>
              )}
            </PrsButtonStackLg>
          </form>
        </MfaPanelRoot>
      </StepTransitionOverlay>
      {pendingCancelAction && (
        <MfaCancelDialog
          isOpen
          body="You haven't completed your two-factor authentication. If you exit now, you'll need to start over."
          onConfirm={() => {
            const action = pendingCancelAction;
            setPendingCancelAction(null);
            if (action) action();
          }}
          onDismiss={() => setPendingCancelAction(null)}
        />
      )}
    </>
  );
};

MfaVerifyPanel.defaultProps = {
  maskedPhone: null,
  expiresIn: null,
  showRememberDevice: true,
  onVerifyAnotherWay: null,
};

MfaVerifyPanel.propTypes = {
  maskedPhone: PropTypes.string,
  expiresIn: PropTypes.number,
  onSuccess: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  showRememberDevice: PropTypes.bool,
  onVerifyAnotherWay: PropTypes.func,
};

export default MfaVerifyPanel;
